export type FieldErrorsType = Array<{
  type: string;
  message: string;
  param: any[];
}>;

export type ValidationError = Record<string, string[] | ValidationError[]>;

export type APIErrorType = {
  type?: string;
  message?: string;
  status?: number;
  param?: string;
  errors?: FieldErrorsType;
};

export class APIError extends Error {
  status: number;

  type?: string;

  param?: string;

  errors: FieldErrorsType | ValidationError | null | undefined;

  constructor({ type, message, status, errors, param }: APIErrorType) {
    super(message);
    this.name = this.constructor.name;
    this.status = status || 500;
    this.errors = errors;
    this.param = param;
    this.type = type;
  }
}

/**
 * Extracts error message from an error object.
 *
 * @param error - The error object (typically APIError)
 * @param baseMessage - Optional prefix message to prepend (e.g., "Failed to fetch records:")
 * @returns A formatted error message string
 */
/**
 * Checks if an API error indicates that the prefetch token is invalid.
 * - 408: Request Timeout (token expired)
 */
export const isPrefetchTokenInvalid = (error: APIError): boolean => {
  const statusCode = error.status;
  return statusCode === 408;
};

/**
 * Handles a prefetch token query error (HTTP 408). If the error is a prefetch
 * token error with retries remaining, clears the token to trigger recovery and
 * suppresses the error. Otherwise, calls onError so the caller can surface it.
 */
export const handlePrefetchQueryError = (
  error: APIError,
  {
    token,
    attempts,
    maxAttempts = 2,
    clearToken,
    onError,
  }: {
    token: string | undefined;
    attempts: number;
    /** Defaults to 2 (matches MAX_PREFETCH_ATTEMPTS in both prefetch contexts). */
    maxAttempts?: number;
    clearToken: () => void;
    onError?: (error: APIError) => void;
  },
): void => {
  if (!token || !isPrefetchTokenInvalid(error)) {
    onError?.(error);
    return;
  }
  if (attempts >= maxAttempts) {
    onError?.(error);
  }
  clearToken();
};

export const extractErrorMessage = (error: any, baseMessage?: string): string => {
  let errorMessage = baseMessage ? `${baseMessage}: ` : '';

  // Extract error message from various possible error structures
  if (error.errors && Array.isArray(error.errors)) {
    errorMessage += error.errors.join(' ');
  } else if (error.error) {
    errorMessage += error.error;
  } else if (error.message) {
    errorMessage += error.message;
  } else {
    return error;
  }

  return errorMessage;
};
